// MACHINERY V2 (Stroje) — RENDER-CRASH BOUNDARY for the whole v2 sub-tree.
//
// WHY IT EXISTS: on 2026-08-23 a single null field white-screened an entire
// primary route. `catalogAtoms.tsx` called `.toLocaleString()` on a server null
// that a `!== undefined` guard let through; the throw happened during render,
// NOTHING in the tree caught it, and React 18's default for an uncaught render
// error is to unmount the whole root — so the owner saw the catalog skeleton
// paint and then a blank page. The type + guard bugs are fixed at source; this
// exists so the NEXT one degrades to a panel instead of a blank browser tab.
//
// Before this, the entire client had exactly three error boundaries and all
// three were MIDAS artifact panels (midas/components/MidasArtifactPanel.tsx:57,
// .../v2/MidasArtifactPanelV2.tsx:74, .../v3/artifacts/MidasArtifactPaneV3.tsx:88).
// `react-error-boundary` is NOT a dependency. Nothing covered machinery v2.
//
// WHY A CLASS: App.tsx:2/:16 mounts a declarative `<BrowserRouter><Routes>`,
// not a data router, so React Router's `errorElement` / route `ErrorBoundary`
// are unavailable — they are data-router-only. A classic class boundary
// (getDerivedStateFromError + componentDidCatch) is the only option here. Do
// NOT migrate the router to get a fancier one. Shape mirrors the house
// pattern at midas/components/v3/artifacts/MidasArtifactPaneV3.tsx:88-105,
// which is itself already mirrored twice inside MIDAS.
//
// TWO DELIBERATE DEVIATIONS FROM THAT MIRROR — do not "align" either back:
//   1. MIDAS passes the whole `translate` fn into the class as
//      `translate={translate as never}` (its own call site,
//      MidasArtifactPaneV3.tsx:289, needs the cast). This boundary needs only
//      two static strings, so the wrapper below resolves them where hooks are
//      legal and hands the class plain `string`s — same shape, no `as never`.
//   2. All three MIDAS boundaries log `console.warn(..., error.message, ...)`.
//      This one logs `console.error` with the RAW error — see componentDidCatch
//      for why `.message` is the wrong thing to keep.
//
// MOUNTED IN MachineryV2Layout.tsx:87-89 around <Outlet/> — and ONLY <Outlet/>.
// That single insertion covers all six v2 routes (useCustomerRoutes.tsx:196-202)
// and deliberately leaves ChromeNavV4 OUTSIDE the boundary, so a crashed page
// still has working navigation to escape with.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { Component, ErrorInfo, ReactNode } from "react";
import { useLocation } from "react-router-dom";

import useTranslation from "../../../../shared/hooks/useTranslation";
import RetryNotice from "./RetryNotice";

interface ErrorBoundaryV2Props {
  /** Already translated — the class cannot call hooks. */
  title: string;
  hint: string;
  /** Current route path, as a PROP and never as a `key`. Drives the reset —
   *  see getDerivedStateFromProps. */
  pathname: string;
  children: ReactNode;
}

interface ErrorBoundaryV2State {
  hasError: boolean;
  /** Pathname of the most recent render. While the fallback is up it stops
   *  advancing, so when `hasError` is true this IS the path that crashed. */
  pathname: string;
}

class ErrorBoundaryV2Inner extends Component<ErrorBoundaryV2Props, ErrorBoundaryV2State> {
  state: ErrorBoundaryV2State = { hasError: false, pathname: this.props.pathname };

  static getDerivedStateFromError(_: Error): Partial<ErrorBoundaryV2State> {
    return { hasError: true };
  }

  /**
   * THE CRASH RESET — and the reason it lives INSIDE the class.
   *
   * This started as `key={location.pathname}` on the wrapper below. A `key`
   * remounts the subtree on EVERY v2 pathname change, healthy or not, so
   * `/machinery-v2/:idA` -> `:idB` tore down and rebuilt the profile instead of
   * re-rendering it — re-running every mount effect and discarding the
   * component state that route was deliberately built around: the monotonic
   * stale-response token (MachineProfileV2.tsx:103) and the two `[id]` effects
   * written to handle an IN-PLACE id change (:105-123 fetch + own-fulfilment
   * fallback, :126-129 tab/readiness reset), plus the accumulated telemetry map
   * and its poll interval inside useFleetTelemetry (fleetTelemetry.ts:186/:217).
   * An error-containment mechanism must not change navigation semantics on the
   * healthy path. With no `key` this component reconciles in place, so healthy
   * navigation behaves exactly as it did before the boundary was inserted.
   *
   * The recovery guarantee survives unchanged: a class boundary latches
   * `hasError` forever, so without a reset the user would stay on the fallback
   * after navigating to a healthy route and only a full page reload would clear
   * it. Clearing on "the path changed AND we are currently showing the
   * fallback" IS that reset, narrowed to the only case that needs it — the
   * crash belonged to the route we just left. A healthy navigation takes the
   * other branch and does nothing but record the new path.
   */
  static getDerivedStateFromProps(
    props: ErrorBoundaryV2Props,
    state: ErrorBoundaryV2State
  ): Partial<ErrorBoundaryV2State> | null {
    if (props.pathname === state.pathname) return null;
    return state.hasError
      ? { pathname: props.pathname, hasError: false }
      : { pathname: props.pathname };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // `console.error` with the RAW error object, NOT `error.message`. There is
    // no Sentry / AppInsights / any error-reporting SDK in this client
    // (verified: no such dependency in src/go-to-stock/package.json, no such
    // import under src/, and no window.onerror / unhandledrejection handler
    // anywhere), so this line plus React's own console output is the ENTIRE
    // record of a production crash. The `Error` parameter type is React's and
    // is NOT enforced at runtime: a lib that throws a string or a plain object
    // makes `error.message` `undefined` and takes the stack with it, logging
    // "[MachineryV2] Render error: undefined" and nothing else. The raw value
    // survives both cases; `info.componentStack` is passed separately because a
    // non-Error payload carries no stack of its own.
    // eslint-disable-next-line no-console
    console.error("[MachineryV2] Render error:", error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, overflowY: "auto", padding: "20px 16px" }}>
          {/* RetryNotice, not a bespoke panel, so a crash looks native next to
              every other v2 failure surface. Clearing `hasError` re-mounts the
              children and genuinely re-runs the render that threw — which is
              the promise a rendered retry button makes (RetryNotice.tsx:41-44).
              A deterministic crash simply lands back here; a transient one
              recovers. */}
          <RetryNotice
            title={this.props.title}
            hint={this.props.hint}
            onRetry={() => this.setState({ hasError: false })}
          />
        </div>
      );
    }
    return this.props.children;
  }
}

const ErrorBoundaryV2: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { translate } = useTranslation("myMachinery");
  const location = useLocation();

  // Hooks live here because the class cannot call them: the two crash strings
  // and the current path are resolved once and handed down as plain props.
  return (
    <ErrorBoundaryV2Inner
      pathname={location.pathname}
      title={translate("myMachinery:v2.crash.title")}
      hint={translate("myMachinery:v2.crash.hint")}
    >
      {children}
    </ErrorBoundaryV2Inner>
  );
};

export default ErrorBoundaryV2;
